import { Checkbox, Divider, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useState } from 'react';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import AccordionApplicantForm from 'front-office/components/shared/applicant/form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { SearchApplicantHistorical } from 'shell/models/search-accountholder-historical';
import { FormikErrors, FormikTouched } from 'formik';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { PmiButton } from 'shell/components/shared/button';
import { transformerPersonGeneralityList } from '../generality-historical/transformer-anpr';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ErrorModal from 'shell/components/shared/anomalie-modal';

interface AccordionAccountholderFormProps {
  disabled?: boolean;
  useIntern?: boolean;
  values?: AccordionAccountholderFormVM;
  errors?: FormikErrors<AccordionAccountholderFormVM>;
  touched?: FormikTouched<AccordionAccountholderFormVM>;
  onChangeValues: (values: AccordionAccountholderFormVM) => void;
  onChange: (
    key: keyof AccordionAccountholderFormVM,
    value: AccordionAccountholderFormVM[keyof AccordionAccountholderFormVM]
  ) => void;
  isHistorical?: boolean;
}

const AccordionAccountholderFormComponent = ({
  disabled,
  values,
  onChange,
  onChangeValues,
  errors,
  touched,
  useIntern = false,
  isHistorical,
}: AccordionAccountholderFormProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [openErrorModal, setOpenErrorModal] = useState(false);

  const getAnpr = useHttpClient<HttpResponse<SearchApplicantHistorical>, CertificateFormVM>(
    `${__PERSON_API_URL__}/persona/ricerca-per-certificato-storico`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
        params: {
          controlloMorte: isHistorical ? undefined : true,
        },
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  // Handle same applicant checkbox
  const handleSameApplicant = useEventCallback(() => {
    if (!values?.sameApplicant) {
      onChange('sameApplicant', !values?.sameApplicant);
      return;
    }

    onChangeValues({ sameApplicant: !values?.sameApplicant, account: {} });
  });

  // Handle accountholder
  const onFieldApplicantChange = useEventCallback(
    (
      key: keyof AccordionApplicantFormVM,
      value: AccordionApplicantFormVM[keyof AccordionApplicantFormVM]
    ) => {
      onChange('account', {
        ...(values?.account || {}),
        applicant: {
          ...(values?.account?.applicant || {}),
          [key]: value,
        },
      });
    }
  );

  // Search historical
  const handleSearch = useEventCallback(() => {
    if (
      !values?.account?.applicant?.fiscalCode &&
      !(values?.account?.applicant?.name && values?.account?.applicant?.surname)
    ) {
      return;
    }

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: values?.account?.applicant?.fiscalCode,
        name: values?.account?.applicant?.name,
        surname: values?.account?.applicant?.surname,
        withoutName: values?.account?.applicant?.withoutName,
        withoutSurname: values?.account?.applicant?.withoutSurname,
        gender: values?.account?.applicant?.gender,
        birthday: values?.account?.applicant?.birthDate,
        birthdayChoice: values?.account?.applicant?.birthChoiceDate,
        // se feature auth anpr === 2
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<SearchApplicantHistorical>, CertificateFormVM>
    ).then((response) => {
      const responsePayload = response as CertificateFormVM;

      if (responsePayload.anomaly?.length) {
        setOpenErrorModal(true);
      }

      onChangeValues({
        ...values,
        searchAccountholder: true,
        isAire: responsePayload?.isAire,
        certifiableAccountholder: responsePayload.certifiableAccountholder,
        anomaly: responsePayload.anomaly,
        account: {
          applicant: {
            fiscalCode: responsePayload?.applicant?.fiscalCode,
            gender: responsePayload?.applicant?.gender,
            surname: responsePayload?.applicant?.surname,
            name: responsePayload?.applicant?.name,
            withoutName: responsePayload?.applicant?.withoutName,
            withoutSurname: responsePayload?.applicant?.withoutSurname,
            deathDate: responsePayload?.applicant?.deathDate,
          },
        },
      });
    });
  });

  const accountErrors = errors?.account as FormikErrors<{
    applicant?: FormikErrors<AccordionApplicantFormVM>;
  }>;

  const accountTouched = touched?.account as FormikTouched<{
    applicant?: FormikTouched<AccordionApplicantFormVM>;
  }>;

  return (
    <Fragment>
      <Grid container spacing={0} columnSpacing={{ xs: 0, md: 1 }}>
        <Grid item xs={12} md={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="sameApplicant"
                checked={values?.sameApplicant || false}
                onChange={handleSameApplicant}
                inputProps={{ 'aria-label': "il richiedente coincide con l'intestatario" }}
                disabled={useIntern ? true : disabled || values?.isLocked}
              />
            }
            label="Il richiedente coincide con l'intestatario"
          />
        </Grid>
        {!values?.sameApplicant ? (
          <Fragment>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <AccordionApplicantForm
                disabled={useIntern ? false : disabled}
                values={values?.account?.applicant}
                onChange={onFieldApplicantChange}
                errors={accountErrors?.applicant as FormikErrors<AccordionApplicantFormVM>}
                touched={accountTouched?.applicant as FormikTouched<AccordionApplicantFormVM>}
              />
            </Grid>
            <Grid container spacing={2} item xs={12} display="flex" justifyContent="flex-end">
              <Grid item xs={12} md={4}>
                <PmiTooltipError
                  show={!!errors?.searchAccountholder}
                  message={errors?.searchAccountholder}
                >
                  <span>
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      onClick={handleSearch}
                      fullWidth
                      id="handle-search-historical"
                    >
                      Cerca
                    </PmiButton>
                  </span>
                </PmiTooltipError>
              </Grid>
            </Grid>
          </Fragment>
        ) : undefined}
      </Grid>
      <ErrorModal
        open={openErrorModal}
        onClose={() => {
          setOpenErrorModal(false);
        }}
        dataPersons={values?.anomaly}
      />
    </Fragment>
  );
};

AccordionAccountholderFormComponent.displayName = 'Accordion applicant form component';

const AccordionAccountholderForm = memo(AccordionAccountholderFormComponent);

export default AccordionAccountholderForm;
